import { apiDelete, apiGet, apiPatch, apiPost, apiPostForm } from './client';
import { importAdminSelectionRoster as importSelectionRoster } from './selectionRoster';

function buildQuery(params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

function buildIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function withIdempotency(options = {}) {
  return {
    ...options,
    headers: {
      ...(options.headers || {}),
      'Idempotency-Key': buildIdempotencyKey(),
    },
  };
}

export function getAdminDashboard() {
  return apiGet('/admin/dashboard');
}

export function getAdminUsers({ query, role, is_banned, limit = 50, offset = 0 } = {}) {
  return apiGet(
    `/admin/users${buildQuery({ query, role, is_banned, limit, offset })}`
  );
}

export function getAdminTeams({ query, is_banned, limit = 50, offset = 0 } = {}) {
  return apiGet(
    `/admin/teams${buildQuery({ query, is_banned, limit, offset })}`
  );
}

export function importAdminTeams(file) {
  const formData = new FormData();
  formData.append('file', file);
  return apiPostForm('/admin/teams/import', formData);
}

export function importAdminTeamYufunts(files) {
  const formData = new FormData();
  Array.from(files || []).forEach((file) => {
    formData.append('files', file);
  });
  return apiPostForm('/admin/teams/yufunts/import', formData);
}

export function updateAdminUser(userId, payload) {
  return apiPatch(`/admin/users/${userId}`, payload);
}

export function banAdminUser(userId, reason) {
  return apiPatch(`/admin/users/${userId}/ban`, { reason });
}

export function unbanAdminUser(userId) {
  return apiPatch(`/admin/users/${userId}/unban`);
}

export function deleteAdminUser(userId) {
  return apiDelete(`/admin/users/${userId}`);
}

export function verifyAdminUserEmail(userId) {
  return apiPatch(`/admin/users/${userId}/verify-email`, {});
}

export function importAdminSelectionStatuses(file, { resetMissing = true } = {}) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('reset_missing', String(Boolean(resetMissing)));
  return apiPostForm('/admin/users/selection-status/import', formData);
}

export function importAdminSelectionRoster(file) {
  return importSelectionRoster(file);
}

export function syncAdminSelectionRosterToUsers() {
  return apiPost('/admin/selection-roster/sync-users', {});
}

export function getAdminSchedule({ query, track_label } = {}) {
  return apiGet(`/admin/schedule${buildQuery({ query, track_label })}`);
}

export function importAdminSchedule(file) {
  const formData = new FormData();
  formData.append('file', file);
  return apiPostForm('/admin/schedule/import', formData);
}

export function createAdminScheduleEntry(payload) {
  return apiPost('/admin/schedule', payload);
}

export function updateAdminScheduleEntry(entryId, payload) {
  return apiPatch(`/admin/schedule/${entryId}`, payload);
}

export function deleteAdminScheduleEntry(entryId) {
  return apiDelete(`/admin/schedule/${entryId}`);
}

export function exportAdminArtifacts({ query } = {}) {
  return apiGet(`/admin/artifacts/export${buildQuery({ query })}`, { raw: true });
}

export function updateAdminTeamRosterEntry(entryId, payload) {
  return apiPatch(`/admin/team-roster/${entryId}`, payload);
}

export function deleteAdminTeamRosterEntry(entryId) {
  return apiDelete(`/admin/team-roster/${entryId}`);
}

export function clearAdminTeamRoster() {
  return apiDelete('/admin/team-roster');
}

export function depositAdminUser(userId, amount, comment = null) {
  return apiPost(
    `/admin/users/${userId}/deposit`,
    { amount: Number(amount), comment },
    withIdempotency()
  );
}

export function deductAdminUser(userId, amount, comment = null) {
  return apiPost(
    `/admin/users/${userId}/deduct`,
    { amount: Number(amount), comment },
    withIdempotency()
  );
}

export function updateAdminTeam(teamId, payload) {
  return apiPatch(`/admin/teams/${teamId}`, payload);
}

export function banAdminTeam(teamId, reason) {
  return apiPatch(`/admin/teams/${teamId}/ban`, { reason });
}

export function unbanAdminTeam(teamId) {
  return apiPatch(`/admin/teams/${teamId}/unban`);
}

export function deleteAdminTeam(teamId) {
  return apiDelete(`/admin/teams/${teamId}`);
}

export function getAdminTeamMembers(teamId) {
  return apiGet(`/admin/teams/${teamId}/members`);
}

export function addAdminTeamMember(teamId, userId, customRole = null) {
  return apiPost(`/admin/teams/${teamId}/members`, {
    user_id: Number(userId),
    custom_role: customRole || null,
  });
}

export function removeAdminTeamMember(teamId, userId) {
  return apiDelete(`/admin/teams/${teamId}/members/${Number(userId)}`);
}

export function setAdminTeamCaptain(teamId, userId) {
  return apiPatch(`/admin/teams/${teamId}/captain/${Number(userId)}`, {});
}

export function depositAdminTeam(teamId, amount, comment = null) {
  return apiPost(
    `/admin/teams/${teamId}/deposit`,
    { amount: Number(amount), comment },
    withIdempotency()
  );
}

export function deductAdminTeam(teamId, amount, comment = null) {
  return apiPost(
    `/admin/teams/${teamId}/deduct`,
    { amount: Number(amount), comment },
    withIdempotency()
  );
}

export function getAdminTeamCases(teamId) {
  return apiGet(`/admin/teams/${teamId}/cases`);
}

export function createAdminTeamCase(teamId, payload) {
  return apiPost(`/admin/teams/${teamId}/cases`, payload);
}

export function updateAdminTeamCase(caseId, payload) {
  return apiPatch(`/admin/team-cases/${caseId}`, payload);
}

export function deleteAdminTeamCase(caseId) {
  return apiDelete(`/admin/team-cases/${caseId}`);
}

export function getAdminAchievements({ query, limit = 100, offset = 0 } = {}) {
  return apiGet(`/admin/achievements${buildQuery({ query, limit, offset })}`);
}

export function createAdminAchievement(payload) {
  return apiPost('/admin/achievements', payload);
}

export function updateAdminAchievement(achievementId, payload) {
  return apiPatch(`/admin/achievements/${achievementId}`, payload);
}

export function deleteAdminAchievement(achievementId) {
  return apiDelete(`/admin/achievements/${achievementId}`);
}

export function assignAdminAchievementToUser(achievementId, userId) {
  return apiPost(`/admin/achievements/${achievementId}/users/${userId}`);
}

export function revokeAdminAchievementFromUser(achievementId, userId) {
  return apiDelete(`/admin/achievements/${achievementId}/users/${userId}`);
}

export function getAdminShowcaseCases({ query, partner, track, is_published, limit = 25, offset = 0 } = {}) {
  return apiGet(`/admin/showcase/cases${buildQuery({ query, partner, track, is_published, limit, offset })}`);
}

export function createAdminShowcaseCase(payload) {
  return apiPost('/admin/showcase/cases', payload);
}

export function updateAdminShowcaseCase(caseId, payload) {
  return apiPatch(`/admin/showcase/cases/${caseId}`, payload);
}

export function deleteAdminShowcaseCase(caseId) {
  return apiDelete(`/admin/showcase/cases/${caseId}`);
}

export function importAdminShowcaseCases(file) {
  const formData = new FormData();
  formData.append('file', file);
  return apiPostForm('/admin/showcase/cases/import', formData);
}

export function getAdminShowcasePrioritySelections({ query, limit = 100, offset = 0 } = {}) {
  return apiGet(`/admin/showcase/priority-selections${buildQuery({ query, limit, offset })}`);
}

export function exportAdminTeamMembers() {
  return apiGet('/admin/exports/team-members', { raw: true });
}

export function exportAdminScheduleEntry(entryId) {
  return apiGet(`/digital-trace-review/tasks/${entryId}/export`, { raw: true });
}
